'use client';

import { useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import useSWR from 'swr';
import { ApiError } from '@/lib/api';
import { memoriesApi } from '@/lib/chat-api';
import type { ChatMemoryList } from '@/types/chat';
import { VOLATILE_SWR_OPTIONS, useAuthedFetcher } from './swrConfig';

const KEY = 'chat-memories';

// A memory said in the chat a moment ago must be in the list when Perfil opens, so the list is
// read on every mount and focus. A 404 is a Worker without the route (or none, the api's origin
// answering): no retry, and the section stays hidden.
const OPTIONS = {
  ...VOLATILE_SWR_OPTIONS,
  shouldRetryOnError: (error: unknown) => !(error instanceof ApiError && error.status === 404),
};

function without(list: ChatMemoryList | undefined, id: string): ChatMemoryList | undefined {
  if (!list) return list;
  const memories = list.memories.filter((memory) => memory.id !== id);
  return { ...list, memories, total: list.total - (list.memories.length - memories.length) };
}

/**
 * What the chat remembers about the person, keyed by account so another one on the same device
 * never sees it, and `forget`, which drops a memory at once and puts it back if the Worker
 * refuses. A memory already gone (forgotten in the chat meanwhile) counts as forgotten.
 */
export function useChatMemories() {
  const { userId } = useAuth();
  const authedFetch = useAuthedFetcher();
  const query = useSWR<ChatMemoryList, Error>(
    userId ? [KEY, userId] : null,
    () => authedFetch((token) => memoriesApi.list(token)),
    OPTIONS,
  );
  const { mutate } = query;

  const forget = useCallback(
    async (id: string) => {
      await mutate(
        async (current) => {
          try {
            await authedFetch((token) => memoriesApi.delete(token, id));
          } catch (error) {
            if (!(error instanceof ApiError && error.status === 404)) throw error;
          }
          return without(current, id);
        },
        {
          optimisticData: (current) => without(current, id) as ChatMemoryList,
          rollbackOnError: true,
          populateCache: true,
          revalidate: false,
        },
      );
    },
    [authedFetch, mutate],
  );

  return { ...query, forget };
}
